import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { LockKeyhole, ShieldCheck } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { buildApiUrl } from "../config/api";
import { isAdminAuthenticated, setAdminSession } from "../utils/authStorage";

function LoginPage() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isAdminAuthenticated()) {
      navigate("/admin/testimonials", { replace: true });
    }
  }, [navigate]);

  const redirectTo = location.state?.from?.pathname || "/admin/testimonials";

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      const response = await fetch(buildApiUrl("/auth/login"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          identifier: identifier.trim(),
          password
        })
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || "Invalid credentials. Please try again.");
      }

      setAdminSession({
        token: payload.token,
        admin: payload.admin
      });
      navigate(redirectTo, { replace: true });
    } catch (submitError) {
      setError(submitError.message || "Login failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Login | NextEra Abroad</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <main className="min-h-screen bg-soft-white px-6 py-10">
        <div className="mx-auto flex min-h-[calc(100vh-5rem)] w-full max-w-5xl items-center justify-center">
          <div className="grid w-full overflow-hidden rounded-[2rem] border border-midnight-navy/12 bg-white shadow-luxury md:grid-cols-[1.05fr_0.95fr]">
            <div className="relative hidden overflow-hidden bg-gradient-to-br from-midnight-navy via-royal-blue/90 to-[#8db0ff] p-10 md:block">
              <div className="hero-grid absolute inset-0 opacity-25" />
              <div className="relative">
                <p className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-sky-glow">
                  <ShieldCheck className="h-3.5 w-3.5 text-warm-gold" aria-hidden="true" />
                  Secure Admin Access
                </p>
                <h1 className="mt-6 text-4xl leading-tight text-soft-white">
                  Manage Student Testimonials with Clarity
                </h1>
                <p className="mt-4 text-base leading-relaxed text-sky-glow/85">
                  This area is for internal content updates only. Public visitors cannot access this section without
                  valid admin login.
                </p>
              </div>
            </div>

            <div className="p-6 sm:p-8 md:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-royal-blue">Manual Login</p>
              <h2 className="mt-3 text-3xl leading-tight">Admin Sign In</h2>
              <p className="mt-2 text-sm text-slate-text/75">
                Access the testimonials manager to add, edit, or remove student stories.
              </p>

              <form className="mt-8 space-y-5" onSubmit={handleSubmit} noValidate>
                <label htmlFor="identifier" className="block text-sm font-medium text-midnight-navy">
                  Username or Email
                  <input
                    id="identifier"
                    name="identifier"
                    type="text"
                    value={identifier}
                    onChange={(event) => setIdentifier(event.target.value)}
                    className="mt-2 w-full rounded-xl border border-midnight-navy/15 bg-white px-4 py-3 text-sm text-midnight-navy focus:border-royal-blue focus:outline-none focus:ring-2 focus:ring-royal-blue/25"
                    required
                    autoComplete="username"
                  />
                </label>

                <label htmlFor="password" className="block text-sm font-medium text-midnight-navy">
                  Password
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="mt-2 w-full rounded-xl border border-midnight-navy/15 bg-white px-4 py-3 text-sm text-midnight-navy focus:border-royal-blue focus:outline-none focus:ring-2 focus:ring-royal-blue/25"
                    required
                    autoComplete="current-password"
                  />
                </label>

                {error ? <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-midnight-navy px-6 py-3.5 text-sm font-semibold text-soft-white shadow-soft hover:bg-royal-blue"
                >
                  <LockKeyhole className="h-4 w-4" aria-hidden="true" />
                  {isSubmitting ? "Signing In..." : "Sign In to Admin Panel"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default LoginPage;
